/**
 * The narrative ticker — ~80 non-repeating lines in the AI's own voice that
 * keep the reader company through the wait. After 4s with work still
 * running it starts, then rolls every 6s until the pipeline goes quiet.
 *
 * It is company for the WAIT only: the call site drops it the moment the
 * draft starts streaming, because the model's own words are the better
 * thing to watch (Brian, UAT 2026-09-21).
 */

import { useEffect, useRef, useState } from 'react';
import { getNarrative } from './inference-narratives';
import type { NarrativeMeta } from './inference-narratives';

interface InferenceNarrativeProps {
  /** True while any pipeline step is running; false stops and resets it. */
  active: boolean;
  meta?: NarrativeMeta;
}

export function InferenceNarrative({ active, meta }: InferenceNarrativeProps) {
  const index = useNarrativeTicker(active);
  if (index < 0) return null;
  return (
    <p key={index} className="pipeline-narrative">
      {getNarrative(index, meta)}
    </p>
  );
}

/**
 * Start 1.5s into the wait, then roll every 6s. Does NOT reset when the
 * active step changes — it keeps going. Only stops when no step is running.
 *
 * The old 4s delay was tuned for a ticker that ran the whole generation;
 * now that it yields the moment the model starts writing, a long delay
 * meant it barely appeared at all (Brian, UAT 2026-09-21).
 */
function useNarrativeTicker(hasRunningStep: boolean): number {
  const [index, setIndex] = useState(-1);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!hasRunningStep) {
      setIndex(-1);
      startedRef.current = false;
      return;
    }
    if (startedRef.current) return;
    const delay = setTimeout(() => {
      startedRef.current = true;
      setIndex(0);
    }, 1500);
    return () => clearTimeout(delay);
  }, [hasRunningStep]);

  useEffect(() => {
    if (index < 0) return;
    const interval = setInterval(() => setIndex((i) => i + 1), 6000);
    return () => clearInterval(interval);
  }, [index >= 0]);

  return index;
}
